import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { FaTiktok } from "react-icons/fa";
import LanguageSelector from "./LanguageSelector";
import MenuImageSlideshow from "./MenuImageSlideshow";
import { useLanguage } from "@/contexts/LanguageContext";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-2 text-sm uppercase tracking-wide transition-colors hover:text-primary ${
    isActive ? "text-primary" : "text-foreground"
  }`;

const menuItems = [
  { 
    label: 'Produse', 
    href: '/products/doors',
    category: 'produse',
  },
  { 
    label: 'Proiecte', 
    href: '/projects',
    category: 'proiecte',
  },
  { 
    label: 'Semifabricate', 
    href: '/semifabricate',
    category: 'semifabricate',
  },
  { 
    label: 'Producție', 
    href: '/production',
    category: 'productie',
  },
  { 
    label: 'Unde ne găsești', 
    href: '/find-us',
    category: 'unde-ne-gasesti',
  },
  { 
    label: 'Contact', 
    href: '/contact',
    category: 'contact',
  },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const { pathname } = useLocation();
  const isDoorsPage = pathname === "/products/doors";
  const isProjectsPage = pathname === "/projects";
  const isDarkHeader = isDoorsPage || isProjectsPage;
  const { t } = useLanguage();

  const handleCloseMenu = () => {
    setMenuClosing(true);
    setTimeout(() => {
      setMenuOpen(false);
      setMenuClosing(false);
    }, 600); // Match animation duration
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 20);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      // Calculate scrollbar width before hiding overflow
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
      setScrollbarWidth(scrollbarW);
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarW}px`;
    } else {
      setScrollbarWidth(0);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header 
        className={`fixed top-0 z-50 w-full transition-all duration-400 ease-in-out ${
          isScrolled ? "-translate-y-full" : ""
        } ${
          isDarkHeader ? "bg-black" : "bg-background/90 backdrop-blur-sm"
        }`}
        style={{ paddingRight: menuOpen ? `${scrollbarWidth}px` : '0' }}
      >
        <div className="container flex items-center justify-between h-20 px-6">
          {/* Left: PRODUCATOR AUTOHTON text */}
          <div className="flex-1">
            <span className={`text-sm md:text-lg font-bodoni font-light tracking-wider ${
              isDarkHeader ? "text-white" : "text-foreground"
            }`}>PRODUCATOR AUTOHTON</span>
          </div>

          <div className="flex-1 flex justify-center items-start">
            <Link to="/" className="hover-scale z-[60] -mt-2" aria-label="MagicDoor home">
              {isDarkHeader ? (
                <img 
                  src="/lovable-uploads/logo-white-new.webp" 
                  alt="MagicDoor logo" 
                  className="h-16 md:h-20 w-auto" 
                  loading="eager"
                  width="128"
                  height="128"
                />
              ) : (
                <img 
                  src="/lovable-uploads/logo-black-text.webp" 
                  alt="MagicDoor logo" 
                  className="h-12 md:h-16 w-auto" 
                  loading="eager"
                  width="128"
                  height="128"
                />
              )}
            </Link>
          </div>

          {/* Right: Nav links + Language + Burger */}
          <div className="flex-1 flex items-center justify-end gap-4">
            <nav className={`hidden lg:flex items-center gap-6 transition-all duration-400 ease-in-out ${isScrolled ? "opacity-0 translate-x-4 pointer-events-none" : "opacity-100 translate-x-0"}`}>
              <NavLink to="/products/doors" className={({ isActive }) => 
                `px-3 py-2 text-sm uppercase tracking-wide transition-colors hover:text-primary ${
                  isActive ? "text-primary" : (isDarkHeader ? "text-white" : "text-foreground")
                }`
              }>
                {t('nav.products')}
              </NavLink>
              <NavLink to="/projects" className={({ isActive }) => 
                `px-3 py-2 text-sm uppercase tracking-wide transition-colors hover:text-primary ${
                  isActive ? "text-primary" : (isDarkHeader ? "text-white" : "text-foreground")
                }`
              }>
                {t('nav.projects')}
              </NavLink>
              <LanguageSelector isDoorsPage={isDarkHeader} />
            </nav>
            
            {/* Burger Button - No morph */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`group inline-flex items-center justify-center w-10 h-10 transition-all duration-400 ease-in-out ml-4 hover:scale-110 ${
                menuOpen ? 'bg-background/90 backdrop-blur-md rounded-lg border border-border/50' : ''
              }`}
              style={{
                background: isScrolled && !menuOpen ? 'hsl(var(--background) / 0.9)' : menuOpen ? 'hsl(var(--background) / 0.9)' : 'transparent',
                borderRadius: isScrolled || menuOpen ? '0.5rem' : '0',
                backdropFilter: isScrolled || menuOpen ? 'blur(8px)' : 'none',
                border: isScrolled || menuOpen ? '1px solid hsl(var(--border))' : 'none',
              }}
              aria-label="Toggle menu"
            >
              <Menu className={`w-6 h-6 transition-all duration-300 ease-in-out group-hover:rotate-90 group-hover:text-primary ${
                isDarkHeader && !menuOpen ? "text-white" : "text-foreground"
              }`} />
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen menu overlay */}
      {menuOpen && (
        <div 
          className={`fixed inset-0 z-[70] bg-background overflow-hidden ${menuClosing ? 'animate-curtain-rise' : 'animate-curtain-drop'}`}
          style={{ 
            animationFillMode: 'forwards',
          }}
        >
          {/* Mobile background videos */}
          <div className="md:hidden absolute inset-0 overflow-hidden">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-1/2 object-cover opacity-30"
            >
              <source src="/lovable-uploads/menu-mobile-video-1.mp4" type="video/mp4" />
            </video>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute bottom-0 w-full h-1/2 object-cover opacity-30"
            >
              <source src="/lovable-uploads/menu-mobile-video-2.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Split-screen layout */}
          <div className="h-screen w-full flex flex-col md:flex-row relative overflow-hidden">
            {/* Mobile: Logo at top */}
            <div className="md:hidden absolute top-8 left-1/2 -translate-x-1/2 z-30">
              <button 
                onClick={handleCloseMenu}
                className="hover:opacity-80 transition-opacity"
                aria-label="Close menu"
              >
                <img 
                  src="/lovable-uploads/logo-black-text.webp" 
                  alt="MagicDoor" 
                  className="h-12 w-auto"
                />
              </button>
            </div>

            {/* Left side - Navigation */}
            <div className="w-full md:w-[40%] flex flex-col justify-center items-center md:items-start px-4 sm:px-8 md:px-16 py-20 md:py-8 relative overflow-hidden z-10">
              {/* Menu items */}
              <nav className="w-full">
                <ul className="space-y-6 md:space-y-8">
                  {menuItems.map((item, index) => (
                    <li 
                      key={item.href}
                      className={`relative ${menuClosing ? 'opacity-100 animate-menu-item-out' : 'opacity-0 animate-menu-item'}`}
                      style={{ 
                        animationDelay: menuClosing ? `${(menuItems.length - index - 1) * 50}ms` : `${index * 100}ms`,
                        animationFillMode: 'forwards'
                      }}
                      onMouseEnter={() => setHoveredCategory(item.category)}
                      onMouseLeave={() => setHoveredCategory(null)}
                    >
                      <NavLink 
                        to={item.href} 
                        className={({ isActive }) => {
                          const isHovered = hoveredCategory === item.category;
                          return `
                            block text-xl sm:text-2xl md:text-3xl font-josefin uppercase tracking-wide text-center md:text-left
                            transition-all duration-400 ease-out relative md:pl-12 story-link
                            ${isActive ? "text-primary" : "text-foreground"}
                            ${isHovered ? 'scale-115 text-primary italic' : 'scale-100'}
                          `;
                        }}
                        onClick={() => setMenuOpen(false)}
                      >
                        {/* Arrow indicator - hidden on mobile */}
                        <span 
                          className={`hidden md:block absolute left-0 top-1/2 -translate-y-1/2 text-primary transition-all duration-300 text-4xl ${
                            hoveredCategory === item.category 
                              ? 'opacity-100 translate-x-0' 
                              : 'opacity-0 -translate-x-5'
                          }`}
                        >
                          →
                        </span>
                        
                        <span className="relative z-10">{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Language selector and Social media - Mobile at bottom, Desktop language only */}
              <div 
                className={`mt-12 md:mt-16 flex flex-col items-center md:items-start gap-6 ${menuClosing ? 'opacity-100 animate-menu-item-out' : 'opacity-0 animate-menu-item'}`}
                style={{ 
                  animationDelay: menuClosing ? '0ms' : '500ms',
                  animationFillMode: 'forwards'
                }}
              >
                <LanguageSelector variant="menu" />
                
                {/* Social buttons - Mobile only */}
                <div className="md:hidden flex items-center gap-4">
                  <a
                    href="https://www.instagram.com/magicdoor.ro/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-black/10 border border-border flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-black/20"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@magicdoor1999"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-black/10 border border-border flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-black/20"
                    aria-label="TikTok"
                  >
                    <FaTiktok className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                  <a
                    href="https://www.facebook.com/magicdoor.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-black/10 border border-border flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-black/20"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                </div>
              </div>

            </div>

            {/* Right side - Image slideshow */}
            <div 
              className={`hidden md:block w-full md:w-[60%] h-64 md:h-full relative ${menuClosing ? 'opacity-100 animate-fade-out' : 'opacity-0 animate-fade-in'}`}
              style={{ 
                animationDelay: menuClosing ? '0ms' : '300ms',
                animationFillMode: 'forwards'
              }}
            >
              <MenuImageSlideshow category={hoveredCategory} />
              
              {/* Logo and Social media buttons - Top Right */}
              <div 
                className={`absolute top-8 right-8 flex items-center gap-6 z-20 ${menuClosing ? 'opacity-100 animate-fade-out' : 'opacity-0 animate-fade-in'}`}
                style={{ 
                  animationDelay: menuClosing ? '0ms' : '600ms',
                  animationFillMode: 'forwards'
                }}
              >
                {/* Logo - Clickable to close menu */}
                <button 
                  onClick={handleCloseMenu}
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Close menu"
                >
                  <img 
                    src="/lovable-uploads/logo-black-text.webp" 
                    alt="MagicDoor" 
                    className="h-12 w-auto opacity-90 drop-shadow-lg cursor-pointer"
                  />
                </button>
                
                {/* Social buttons */}
                <div className="flex items-center gap-4">
                  <a
                    href="https://www.instagram.com/magicdoor.ro/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-white/10 border border-white/30 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-white/20 hover:shadow-[0_0_20px_rgba(var(--primary),0.3)]"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@magicdoor1999"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-white/10 border border-white/30 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-white/20 hover:shadow-[0_0_20px_rgba(var(--primary),0.3)]"
                    aria-label="TikTok"
                  >
                    <FaTiktok className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                  <a
                    href="https://www.facebook.com/magicdoor.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full backdrop-blur-md bg-white/10 border border-white/30 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-white/20 hover:shadow-[0_0_20px_rgba(var(--primary),0.3)]"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 text-foreground hover:text-primary transition-colors" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
